import React, { useState } from "react";
import { Mail, MapPin, Phone, Clock, Loader2, CheckCircle2, Linkedin } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Container, PageHero, Reveal, SectionHeading } from "../components/shared/Shared";
import { submitContact, getErrorMessage } from "../lib/api";
import { useLang } from "../context/LanguageContext";

const initial = { name: "", email: "", phone: "", subject: "", message: "" };

export const Contact = () => {
  const { t, c } = useLang();
  const k = t.contact;
  const { COMPANY } = c;
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      setDone(true);
      toast.success(k.toast);
    } catch (err) {
      toast.error(getErrorMessage(err, t.common.error));
    } finally {
      setLoading(false);
    }
  };

  const info = [
    { Icon: MapPin, label: k.visit, value: COMPANY.address },
    { Icon: Phone, label: k.call, value: COMPANY.phone, href: COMPANY.phoneHref, ltr: true },
    { Icon: Mail, label: k.emailUs, value: COMPANY.email, href: `mailto:${COMPANY.email}` },
    { Icon: Clock, label: k.hours, value: COMPANY.hours },
  ];

  return (
    <>
      <PageHero title={k.title} subtitle={k.subtitle} crumbs={[{ label: k.title }]} image={c.IMAGES.about} />
      <section className="py-24" data-testid="contact-page">
        <Container className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow={k.eyebrow} title={k.heading} description={k.desc} />
            <div className="mt-10 space-y-4">
              {info.map((i, idx) => (
                <Reveal key={i.label} delay={idx * 70} className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-5">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-teal/10 text-brand-teal"><i.Icon className="h-5 w-5" /></span>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{i.label}</p>
                    {i.href ? <a href={i.href} className="mt-1 block font-semibold text-brand-navy hover:text-brand-teal" dir={i.ltr ? "ltr" : undefined} style={i.ltr ? { textAlign: "start" } : undefined}>{i.value}</a> : <p className="mt-1 font-semibold text-brand-navy">{i.value}</p>}
                  </div>
                </Reveal>
              ))}
              <Reveal delay={300}><a href={COMPANY.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-teal hover:text-brand-teal-dark"><Linkedin className="h-4 w-4" /> {k.follow}</a></Reveal>
            </div>
          </div>
          <Reveal delay={120} className="lg:col-span-7">
            <div className="rounded-3xl bg-white p-8 shadow-xl shadow-brand-navy/5 ring-1 ring-slate-100 sm:p-10">
              {done ? (
                <div className="flex flex-col items-center py-12 text-center" data-testid="contact-success">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><CheckCircle2 className="h-8 w-8" /></span>
                  <h3 className="mt-5 font-heading text-2xl font-bold text-brand-navy">{k.thanks}, {form.name.split(" ")[0]}!</h3>
                  <p className="mt-2 max-w-sm text-slate-600">{k.success}</p>
                  <button type="button" onClick={() => { setDone(false); setForm(initial); }} className="btn-outline mt-8">{k.another}</button>
                </div>
              ) : (
                <>
                  <h3 className="font-heading text-2xl font-bold text-brand-navy">{k.support}</h3>
                  <p className="mt-1 text-sm text-slate-500">{k.required}</p>
                  <form onSubmit={submit} className="mt-8 grid gap-5 sm:grid-cols-2" data-testid="contact-form">
                    <div className="space-y-1.5"><Label htmlFor="c-name">{k.name}</Label><Input id="c-name" required value={form.name} onChange={set("name")} data-testid="contact-name-input" className="h-12" /></div>
                    <div className="space-y-1.5"><Label htmlFor="c-email">{k.email}</Label><Input id="c-email" type="email" required value={form.email} onChange={set("email")} placeholder="name@company.com" data-testid="contact-email-input" className="h-12" /></div>
                    <div className="space-y-1.5"><Label htmlFor="c-phone">{k.phone}</Label><Input id="c-phone" value={form.phone} onChange={set("phone")} placeholder="+971 50 000 0000" data-testid="contact-phone-input" className="h-12" /></div>
                    <div className="space-y-1.5"><Label htmlFor="c-subject">{k.subject}</Label><Input id="c-subject" value={form.subject} onChange={set("subject")} placeholder={k.subjectPh} data-testid="contact-subject-input" className="h-12" /></div>
                    <div className="space-y-1.5 sm:col-span-2"><Label htmlFor="c-message">{k.message}</Label><Textarea id="c-message" required rows={6} value={form.message} onChange={set("message")} placeholder={k.messagePh} data-testid="contact-message-input" /></div>
                    <div className="sm:col-span-2"><button type="submit" disabled={loading} className="btn-primary w-full bg-brand-orange text-white hover:bg-orange-600 disabled:opacity-60 sm:w-auto" data-testid="contact-submit-button">{loading ? <><Loader2 className="h-4 w-4 animate-spin" /> {k.sending}</> : k.submit}</button></div>
                  </form>
                </>
              )}
            </div>
          </Reveal>
        </Container>
      </section>
      <section className="pb-24" data-testid="map-section">
        <Container>
          <Reveal className="overflow-hidden rounded-3xl shadow-xl shadow-brand-navy/5 ring-1 ring-slate-100">
            <iframe title="Jonaren Technologies And Trading LLC location" src="https://www.google.com/maps?q=Shams+Business+Centre,+Sharjah+Media+City+Free+Zone,+Al+Messaned,+Sharjah,+United+Arab+Emirates&output=embed" className="h-[420px] w-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
          </Reveal>
        </Container>
      </section>
    </>
  );
};
